/** Matches legacy-rem.scss defaults ($legacy-base, $new-base). */
export const DEFAULT_LEGACY_REM_BASE_PX = 7;
export const DEFAULT_NEW_REM_BASE_PX = 16;

/**
 * Breakpoints from _responsive.scss, most-specific first.
 * Each entry describes the legacy root font-size that applied in that context.
 */
export const LEGACY_REM_BREAKPOINTS: readonly {
  minWidth?: number;
  minDpr?: number;
  basePx: number;
}[] = [
  { minDpr: 2, basePx: 6.5 }, // retina (@media min-device-pixel-ratio: 2)
  { minWidth: 1400, basePx: 7 }, // medium + large desktop
  { basePx: 6 }, // default: phones / tablets / standard desktop
];

/**
 * Return the legacy root font-size (px) that would have applied at the given
 * viewport dimensions.  Mirrors the media-query breakpoints in _responsive.scss.
 */
export function resolveLegacyBasePx(viewportWidth?: number, devicePixelRatio?: number): number {
  if (devicePixelRatio !== undefined && devicePixelRatio >= 2) return 6.5;
  if (viewportWidth !== undefined && viewportWidth >= 1400) return 7;
  return 6;
}

export interface LegacyRemOptions {
  /** Override the legacy root font-size directly (px). */
  legacyBasePx?: number;
  /** Override the new root font-size (px). Defaults to 16 (browser default). */
  newBasePx?: number;
  /** When `false`, returns the input CSS unchanged. Defaults to `true`. */
  enabled?: boolean;
  /**
   * Viewport width in CSS pixels.  When omitted and running in a browser,
   * `window.innerWidth` is used automatically.
   */
  viewportWidth?: number;
  /**
   * Device pixel ratio.  When omitted and running in a browser,
   * `window.devicePixelRatio` is used automatically — retina displays (dpr ≥ 2)
   * resolve to the 6.5 px legacy base rather than the 7 px large-desktop default.
   */
  devicePixelRatio?: number;
}

const LEGACY_REM_TOKEN_RE = /(?<![.\w])(-?\d*\.?\d+)rem\b/g;

function resolveBases(options?: LegacyRemOptions): { legacyBasePx: number; newBasePx: number } {
  let legacyBasePx: number;

  if (options?.legacyBasePx !== undefined) {
    legacyBasePx = options.legacyBasePx;
  } else if (options?.viewportWidth !== undefined || options?.devicePixelRatio !== undefined) {
    legacyBasePx = resolveLegacyBasePx(options.viewportWidth, options.devicePixelRatio);
  } else if (typeof window !== 'undefined') {
    // Auto-detect from the browser environment — ensures retina displays (e.g. 1440×900 MacBook Pro,
    // window.devicePixelRatio=2) use 6.5px rather than the 7px large-desktop default.
    legacyBasePx = resolveLegacyBasePx(window.innerWidth, window.devicePixelRatio);
  } else {
    legacyBasePx = DEFAULT_LEGACY_REM_BASE_PX;
  }

  return {
    legacyBasePx,
    newBasePx: options?.newBasePx ?? DEFAULT_NEW_REM_BASE_PX,
  };
}

/** Convert a rem count authored for the legacy root to rem at the new root (unitless). */
export function legacyRem(size: number, options?: LegacyRemOptions): number {
  const { legacyBasePx, newBasePx } = resolveBases(options);
  return (size * legacyBasePx) / newBasePx;
}

function formatRemNumber(value: number): string {
  const rounded = Math.round(value * 1e6) / 1e6;
  const asString = String(rounded);

  if (!asString.includes('.')) {
    return asString;
  }

  return asString.replace(/\.?0+$/, '');
}

/** Same as legacyRem, formatted as a CSS length (e.g. `8.75rem`). */
export function legacyRemCss(size: number, options?: LegacyRemOptions): string {
  return `${formatRemNumber(legacyRem(size, options))}rem`;
}

/** Replace literal `Nrem` tokens in a CSS/SCSS string (e.g. server `custom_css`). */
export function convertLegacyRemInCss(css: string, options?: LegacyRemOptions): string {
  const enabled = options?.enabled ?? true;

  if (!enabled) {
    return css;
  }

  return css.replace(LEGACY_REM_TOKEN_RE, (match, value: string) => {
    const size = Number.parseFloat(value);

    if (Number.isNaN(size)) {
      return match;
    }

    return legacyRemCss(size, options);
  });
}
